import { useState, useEffect, useRef, useCallback } from 'react';
import { ExternalLink, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FeedItem } from '@/types/headerFeed';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface NewsTickerProps {
  items: FeedItem[];
  className?: string;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function getCategoryColor(category: FeedItem['category']): string {
  switch (category) {
    case 'breaking': return 'text-destructive';
    case 'ai': return 'text-purple-500';
    case 'cyber': return 'text-cyan-500';
    default: return 'text-muted-foreground';
  }
}

export function NewsTicker({ items, className }: NewsTickerProps) {
  const [isPaused, setIsPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  
  const handleFocus = useCallback(() => setIsPaused(true), []);
  const handleBlur = useCallback(() => setIsPaused(false), []);
  
  // Timeout: if no items after 5s, show fallback instead of infinite "Loading..."
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (items.length > 0) return;
    const timer = setTimeout(() => setTimedOut(true), 5000);
    return () => clearTimeout(timer);
  }, [items.length]);

  if (items.length === 0) {
    if (timedOut) return null; // Hide ticker gracefully
    return (
      <div className={cn("flex items-center justify-center text-muted-foreground text-sm", className)} aria-busy="true" role="status">
        Loading news…
      </div>
    );
  }
  
  // Reduced motion: show dropdown instead of scrolling
  if (prefersReducedMotion) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1 text-sm">
              Latest News
              <ChevronRight className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-80 max-h-80 overflow-y-auto">
            {items.slice(0, 10).map((item) => (
              <DropdownMenuItem key={item.id} asChild>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col gap-1 py-2"
                >
                  <span className="text-sm font-medium line-clamp-2">{item.title}</span>
                  <span className={cn("text-xs", getCategoryColor(item.category))}>
                    {item.source} • {formatTimeAgo(item.publishedAt)}
                  </span>
                </a>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }
  
  return (
    <div
      ref={containerRef}
      className={cn(
        "relative overflow-hidden flex-1 min-w-0",
        className
      )}
      role="marquee"
      aria-label="News ticker"
      aria-live="off"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div
        ref={contentRef}
        className={cn(
          "flex items-center gap-8 whitespace-nowrap",
          "animate-marquee",
          isPaused && "animation-paused"
        )}
        style={{
          animationDuration: `${Math.max(items.length * 5, 30)}s`,
        }}
      >
        {/* Duplicate items for seamless loop */}
        {[...items, ...items].map((item, index) => (
          <a
            key={`${item.id}-${index}`}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex items-center gap-2 px-3 py-1 rounded-md",
              "hover:bg-muted/50 transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
              "group"
            )}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <span className="text-sm font-medium truncate max-w-xs">
              {item.title}
            </span>
            <span className={cn("text-xs shrink-0", getCategoryColor(item.category))}>
              {item.source}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">
              {formatTimeAgo(item.publishedAt)}
            </span>
            <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
          </a>
        ))}
      </div>
    </div>
  );
}
